import _ from 'lodash';
import React from 'react';

import {
  FactureContext,
  FacturePrelevementContext,
  FactureRejetContext,
  LoadingContext,
  RegieContext,
} from '@/contexts';
import type { FactureContextType } from '@/contexts/FactureContext';
import useListeSearchSort from '@/hooks/useListeSearchSort';
import {
  getFactures,
  getFacturesFull,
  getFacturesPrelevees,
  getFacturesRejets,
  searchFactures,
  searchFacturesFull,
  searchFacturesPrelevees,
  searchFacturesRejets,
} from '@/routes';
import type { FactureType } from '@/types';
import { getFacturesEncaissementListeDefaultFilters } from '@/utils';

type FactureContextProviderProps = {
  children: React.ReactNode;
  type?: 'all' | 'encaissable' | 'prelevee' | 'rejet';
};

const FactureContextProvider = ({
  children,
  type = 'encaissable',
}: FactureContextProviderProps) => {
  const { setFacturesLoadings } = React.useContext(LoadingContext);
  const { selectedRegie } = React.useContext(RegieContext);

  const [defaultFiltersApplied, setDefaultFiltersApplied] = React.useState(false);

  const {
    items,
    setItems,
    setPage,
    loading,
    sorts,
    setSorts,
    filters,
    setFilters,
    total,
    setTotal,
    search,
    setSearch,
    reset,
  } = useListeSearchSort({
    active: !_.isNil(selectedRegie) && defaultFiltersApplied,
    getItems:
      type === 'all'
        ? getFacturesFull
        : type === 'prelevee'
          ? getFacturesPrelevees
          : type === 'rejet'
            ? getFacturesRejets
            : getFactures,
    searchItems:
      type === 'all'
        ? searchFacturesFull
        : type === 'prelevee'
          ? searchFacturesPrelevees
          : type === 'rejet'
            ? searchFacturesRejets
            : searchFactures,
  });

  /** Applique les filtres par défaut */
  const handleDefaultFiltersApply = React.useCallback(() => {
    if (type !== 'all' && type !== 'prelevee' && type !== 'rejet') {
      setFilters(getFacturesEncaissementListeDefaultFilters());
      setDefaultFiltersApplied(true);
    } else {
      setFilters([]);
      setDefaultFiltersApplied(true);
    }
  }, [type, setFilters]);

  React.useEffect(() => {
    setFacturesLoadings((prev) => ({ ...prev, factures: loading }));
  }, [loading, setFacturesLoadings]);

  React.useEffect(() => {
    handleDefaultFiltersApply();
  }, [handleDefaultFiltersApply]);

  const context = React.useMemo<FactureContextType>(
    () => ({
      total,
      factures: items as FactureType[],
      filters,
      search,
      sorts,
      setFactures: setItems,
      setFilters,
      setSearch,
      setSorts,
      setPage,
      setTotal,
      getFactures: reset,
      applyDefaultFilters: handleDefaultFiltersApply,
    }),
    [
      total,
      items,
      filters,
      search,
      sorts,
      setItems,
      setFilters,
      setSearch,
      setSorts,
      setPage,
      setTotal,
      reset,
      handleDefaultFiltersApply,
    ],
  );

  const Context =
    type === 'prelevee'
      ? FacturePrelevementContext
      : type === 'rejet'
        ? FactureRejetContext
        : FactureContext;

  return <Context.Provider value={context}>{children}</Context.Provider>;
};

export default FactureContextProvider;
